import React, { useState, useEffect, useCallback } from 'react';

const WORKER_URL = 'https://rakeen-whatsapp-poc.thamer-21d.workers.dev';

const EVENT_LABELS = {
  login: '🔑 تسجيل دخول',
  logout: '🚪 تسجيل خروج',
  page_view: '👁️ عرض صفحة',
  reply: '💬 رد',
  quote: '📋 عرض سعر',
  advance: '⬅ تقدم طلب',
  menu_save: '💾 حفظ قائمة',
  menu_rollback: '🔄 استرجاع نسخة',
  toggle_test: '🧪 تبديل اختبار',
};

function AdminPanel({ idToken }) {
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [showEvents, setShowEvents] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [summaryRes, eventsRes] = await Promise.all([
        fetch(`${WORKER_URL}/api/analytics/summary?days=${days}`, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        }),
        fetch(`${WORKER_URL}/api/analytics?from=${getFromDate(days)}`, {
          headers: { 'Authorization': `Bearer ${idToken}` },
        }),
      ]);

      if (summaryRes.ok) setSummary(await summaryRes.json());
      if (eventsRes.ok) setEvents(await eventsRes.json());
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }, [idToken, days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function getFromDate(d) {
    const date = new Date();
    date.setDate(date.getDate() - d);
    return date.toISOString().split('T')[0];
  }

  const clearAllData = async () => {
    if (!window.confirm('مسح جميع المحادثات والبيانات؟ هذا لا يمكن التراجع عنه.')) return;
    await fetch(`${WORKER_URL}/api/conversations`, { method: 'DELETE' });
    alert('تم مسح البيانات');
  };

  if (loading) return <div className="sd-admin-loading">جاري التحميل...</div>;

  return (
    <div className="sd-admin">
      {/* Period Selector */}
      <div className="sd-admin-period">
        <span>الفترة:</span>
        {[7, 14, 30].map((d) => (
          <button
            key={d}
            className={`sd-admin-period-btn ${days === d ? 'active' : ''}`}
            onClick={() => { setDays(d); setLoading(true); }}
          >
            {d} يوم
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="sd-admin-stats">
          <div className="sd-admin-stat">
            <span className="sd-admin-stat-number">{summary.totalEvents}</span>
            <span className="sd-admin-stat-label">إجمالي الأحداث</span>
          </div>
          <div className="sd-admin-stat">
            <span className="sd-admin-stat-number">{summary.logins}</span>
            <span className="sd-admin-stat-label">تسجيلات دخول</span>
          </div>
          <div className="sd-admin-stat">
            <span className="sd-admin-stat-number">{summary.replies}</span>
            <span className="sd-admin-stat-label">ردود مرسلة</span>
          </div>
          <div className="sd-admin-stat">
            <span className="sd-admin-stat-number">{summary.quotes}</span>
            <span className="sd-admin-stat-label">عروض أسعار</span>
          </div>
          <div className="sd-admin-stat">
            <span className="sd-admin-stat-number">{summary.advances}</span>
            <span className="sd-admin-stat-label">تقدم طلبات</span>
          </div>
          <div className="sd-admin-stat">
            <span className="sd-admin-stat-number">{summary.uniqueUsers}</span>
            <span className="sd-admin-stat-label">مستخدمين</span>
          </div>
          <div className="sd-admin-stat">
            <span className="sd-admin-stat-number">{summary.uniqueDevices}</span>
            <span className="sd-admin-stat-label">أجهزة مختلفة</span>
          </div>
          <div className="sd-admin-stat">
            <span className="sd-admin-stat-number">{summary.menuSaves}</span>
            <span className="sd-admin-stat-label">تعديلات القائمة</span>
          </div>
        </div>
      )}

      {/* Device & Browser Breakdown */}
      {summary && (
        <div className="sd-admin-breakdowns">
          <div className="sd-admin-breakdown">
            <h4>📱 الأجهزة</h4>
            {Object.entries(summary.deviceBreakdown || {}).map(([device, count]) => (
              <div key={device} className="sd-admin-breakdown-row">
                <span>{device}</span>
                <span>{String(count)}</span>
              </div>
            ))}
            {Object.keys(summary.deviceBreakdown || {}).length === 0 && (
              <span className="sd-admin-empty-text">لا توجد بيانات</span>
            )}
          </div>
          <div className="sd-admin-breakdown">
            <h4>🌐 المتصفحات</h4>
            {Object.entries(summary.browserBreakdown || {}).map(([browser, count]) => (
              <div key={browser} className="sd-admin-breakdown-row">
                <span>{browser}</span>
                <span>{String(count)}</span>
              </div>
            ))}
            {Object.keys(summary.browserBreakdown || {}).length === 0 && (
              <span className="sd-admin-empty-text">لا توجد بيانات</span>
            )}
          </div>
        </div>
      )}

      {/* Activity Log */}
      <div className="sd-admin-section">
        <div className="sd-admin-section-header">
          <h3>📜 سجل النشاطات ({events.length})</h3>
          <button className="sd-admin-toggle-btn" onClick={() => setShowEvents(!showEvents)}>
            {showEvents ? 'إخفاء' : 'عرض'}
          </button>
        </div>

        {showEvents && (
          <div className="sd-admin-events">
            {events.length === 0 ? (
              <div className="sd-admin-empty">لا توجد نشاطات في هذه الفترة</div>
            ) : (
              [...events].reverse().slice(0, 100).map((event, i) => (
                <div key={i} className="sd-admin-event">
                  <span className="sd-admin-event-time">
                    {new Date(event.timestamp).toLocaleString('ar-SA', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' })}
                  </span>
                  <span className="sd-admin-event-type">
                    {EVENT_LABELS[event.type] || event.type}
                  </span>
                  <span className="sd-admin-event-user">{event.email}</span>
                  <span className="sd-admin-event-device">{event.device} / {event.browser}</span>
                  {event.data && (
                    <span className="sd-admin-event-data">
                      {Object.entries(event.data).map(([k, v]) => `${k}: ${v}`).join(' | ')}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Admin Controls */}
      <div className="sd-admin-section">
        <h3>🔧 أدوات الإدارة</h3>
        <div className="sd-admin-controls">
          <div className="sd-admin-control">
            <h4>مسح البيانات</h4>
            <p>مسح جميع المحادثات والطلبات والجلسات. لا يؤثر على القائمة أو الحسابات.</p>
            <button className="sd-admin-danger-btn" onClick={clearAllData}>🗑️ مسح جميع المحادثات</button>
          </div>
          <div className="sd-admin-control">
            <h4>إدارة الحسابات</h4>
            <p>لإضافة أو تعديل حسابات المستخدمين، استخدم لوحة تحكم Firebase.</p>
            <a
              href="https://console.firebase.google.com/project/proshere-2f562/authentication/users"
              target="_blank"
              rel="noopener noreferrer"
              className="sd-admin-link-btn"
            >
              فتح Firebase Console ↗
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AdminPanel;
